/**
 * Getting one design file into the Upload Studio (Phase 91, 91-07; D-01, D-03).
 *
 * Two ways in, one "file ready" state: a dropped or picked PNG (refused
 * before any byte uploads when it is not a PNG, unreadable or too big; then
 * preview, signed PUT, sha256 and commit) or a file from the org's Drive
 * designs folder. The work itself lives in `lib/studio-upload-store.ts`.
 *
 * 91-09: the page owns one store (`StudioUploadStoreProvider` through
 * `useStudioUploadStoreOwner`) so a file keeps uploading after Save and next
 * opens the next product, and the save queue waits on it. Each screen reads
 * its files by key (`uploadKeyOf(productId, side|tone)`). Mounted without a
 * page store (tests, any lone mount), the hook keeps a private store and
 * stops its uploads on unmount, as before.
 *
 * The browser only previews: the server re-reads the stored file and answers
 * its own px, trim and preview (D-01).
 */
import { createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { trpc } from '@/lib/trpc';
import type { StudioErrorLine } from '../lib/studio-errors';
import {
  createStudioUploadStore,
  type DriveImportRequest,
  type StudioUploadProcedures,
  type StudioUploadState,
  type StudioUploadStore,
} from '../lib/studio-upload-store';

export {
  STUDIO_PREVIEW_MAX_SIDE_PX,
  type DriveImportRequest,
  type StudioFile,
  type StudioUploadPhase,
  type StudioUploadState,
} from '../lib/studio-upload-store';

export interface StudioSourceUploadApi {
  state: StudioUploadState;
  /** A dropped or picked file. */
  pick: (file: File) => Promise<void>;
  /** A file from the Drive list; resolves to the refusal line, or null when the file is on the canvas. */
  importFromDrive: (req: DriveImportRequest) => Promise<StudioErrorLine | null>;
  /** Try again after a failed upload. */
  retry: () => Promise<void>;
}

/** The page's upload store; null outside the studio page. */
export const StudioUploadStoreContext = createContext<StudioUploadStore | null>(null);

/** The three upload procedures, from this component's own mutations. */
function useUploadProcedures(): StudioUploadProcedures {
  const mint = trpc.uploadStudio.mintSource.useMutation();
  const commit = trpc.uploadStudio.commitSource.useMutation();
  const importDrive = trpc.uploadStudio.importSourceFromDrive.useMutation();
  return { mint: mint.mutateAsync, commit: commit.mutateAsync, importDrive: importDrive.mutateAsync };
}

/** The page's store: lives as long as the page, so uploads survive moving between products. */
export function useStudioUploadStoreOwner(): StudioUploadStore {
  const procedures = useUploadProcedures();
  const [store] = useState(createStudioUploadStore);
  store.setProcedures(procedures);
  useEffect(() => {
    // Offline pauses an upload; back online resends every paused one.
    const on = () => store.setOnline(true);
    const off = () => store.setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      store.dispose();
    };
  }, [store]);
  return store;
}

/** The store a screen reads: the page's, or a private one. */
function useStore(procedures: StudioUploadProcedures): StudioUploadStore {
  const shared = useContext(StudioUploadStoreContext);
  const [own] = useState(() => (shared ? null : createStudioUploadStore()));
  if (own) own.setProcedures(procedures);
  useEffect(() => () => own?.dispose(), [own]);
  return shared ?? own!;
}

export function useStudioSourceUpload(locationId: string | null, key = 'file'): StudioSourceUploadApi {
  const procedures = useUploadProcedures();
  const store = useStore(procedures);
  const state = useSyncExternalStore(
    store.subscribe,
    () => store.get(key),
    () => store.get(key),
  );
  const pick = useCallback((file: File) => store.pick(key, file, locationId), [key, locationId, store]);
  const importFromDrive = useCallback(
    (req: DriveImportRequest) => store.importFromDrive(key, req, locationId),
    [key, locationId, store],
  );
  const retry = useCallback(() => store.retry(key), [key, store]);
  return { state, pick, importFromDrive, retry };
}
